import { BellRing } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { RenewalReminderState } from "@/hooks/subscription/useRenewalReminder";
import { RenewalShareActions } from "./RenewalShareActions";
import { RenewalTimeline } from "./RenewalTimeline";

interface RenewalReminderModalProps {
  reminder: RenewalReminderState;
}

export function RenewalReminderModal({ reminder }: RenewalReminderModalProps) {
  const navigate = useNavigate();
  const { timing, audience, expiryLabel, billedThroughCenterName } = reminder;

  if (!timing) return null;

  const daysLeftText = `${timing.daysLeft} day${timing.daysLeft !== 1 ? "s" : ""} left`;

  const title =
    audience === "payer"
      ? `${daysLeftText}. Renew now and enjoy uninterrupted access.`
      : audience === "branch"
        ? `${daysLeftText}. Let ${billedThroughCenterName || "your main branch"} know.`
        : `${daysLeftText}. Let the right person know.`;

  const description =
    audience === "payer"
      ? `Your new period still starts on ${expiryLabel}, not today.`
      : "Send a quick reminder to whoever handles billing to prevent interrupted access.";

  const handleRenew = () => {
    reminder.closeModal();
    navigate("/settings?section=subscription");
  };

  return (
    <Dialog
      open={reminder.isModalOpen}
      onOpenChange={(open) => !open && reminder.closeModal()}
    >
      <DialogContent
        className="gap-0 overflow-hidden p-0 sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <div className="space-y-5 bg-accent p-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-card">
              <BellRing className="h-5 w-5 text-accent-foreground" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-accent-foreground">
              FirstRep portal subscription
            </span>
          </div>
          <RenewalTimeline expiryLabel={expiryLabel} />
        </div>

        <div className="space-y-5 p-6">
          <div className="space-y-2 text-left">
            <DialogTitle className="text-xl font-bold leading-tight sm:text-2xl">
              {title}
            </DialogTitle>
            <DialogDescription className="text-sm sm:text-base">
              {description}
            </DialogDescription>
          </div>

          {audience === "payer" ? (
            <div className="space-y-2">
              <Button size="lg" className="w-full font-semibold" onClick={handleRenew}>
                Renew now
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={reminder.closeModal}
              >
                Remind me tomorrow
              </Button>
            </div>
          ) : (
            <RenewalShareActions
              message={reminder.shareMessage}
              onDone={reminder.closeModal}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
